import React, { useEffect, useRef, useState } from 'react';
import { Item } from '@baemingo/table-events/dist/types';
import styled from '@emotion/styled';
import CourseTypeBox from './CourseType';
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { useCurrency, useI18n } from '~Hooks';
import { DragTypes } from './Types';
import { CombinedItem } from '~Utils/combineArticles';
import { BOOKINGS_DISCOUNT_ID } from '~Providers/BookingsProvider';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { Box } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import FlashOverlay from './FlashOverlay';
import useOffers from '~Hooks/useOffers';

const ItemWrapper = styled.li<{
  selected: boolean;
  preview?: boolean;
  isParkedOrder?: boolean;
}>`
  list-style: none;
  position: relative;
  border-bottom: 1px solid
    ${({ isParkedOrder }) =>
      isParkedOrder ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray1200)'};
  padding: 10px 16px 10px 12px;
  border-left: 4px solid;
  border-left-color: ${({ selected, isParkedOrder }) =>
    selected && !isParkedOrder ? 'var(--colorUIYellow)' : 'transparent'};
  align-items: center;

  p {
    padding-right: 6px;
    margin: 0;
    word-break: break-word;
  }

  ${({ preview }) =>
    preview &&
    `
    border: none;
    color: var(--colorBlack);
    background: var(--colorWhite);
    `}
`;

const ItemInnerWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const PriceBox = styled(Box)<{ paddingLeft?: boolean }>`
  padding-left: ${({ paddingLeft }) => (paddingLeft ? '4px' : 'auto')};
  margin-left: auto;
`;

const InitialPrice = styled.span`
  color: var(--colorBlueGray600);
  text-decoration: line-through;
  font-size: 12px;
`;

const DiscountName = styled.span`
  color: var(--colorUIOrange);
  font-size: 10px;
  text-align: right;
  display: block;
  margin-bottom: 2px;
  max-width: 74px;
`;

const Qty = styled.span<{ pulsing?: boolean }>`
  width: 18px;
  margin-right: 8px;
  display: inline-block;
  min-width: 18px;
  transition: transform 0.2s ease;
  transform: ${({ pulsing }) => (pulsing ? 'scale(1.6)' : 'scale(1)')};
`;

interface Props {
  item: Item | CombinedItem<Item>;
  onClick: () => void;
  setIsDragging: (isDragging: boolean) => void;
  selected: boolean;
  selectedItems: string[];
  preview?: boolean;
  hidePrice?: boolean;
  disallowDrag?: boolean;
  addedItemEvent?: { token: number; pulseId?: string; flashId?: string } | null;
}

const ArticleItem: React.FunctionComponent<Props> = ({
  item,
  onClick,
  setIsDragging,
  selected,
  preview,
  selectedItems,
  hidePrice,
  disallowDrag,
  addedItemEvent,
}) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const offers = useOffers();
  const wrapperRef = useRef<HTMLLIElement | null>(null);
  const [pulsing, setPulsing] = useState(false);
  const [flashToken, setFlashToken] = useState<number | null>(null);

  // `addedItemEvent` is a fresh object every time an item is added, so this
  // always reruns - even if the same product id is targeted repeatedly.
  useEffect(() => {
    if (!addedItemEvent) {
      return undefined;
    }

    // Zoom the qty number briefly when this item's quantity was just increased (compressed cart)
    if (addedItemEvent.pulseId === item.id) {
      setPulsing(true);
      const timeout = setTimeout(() => setPulsing(false), 200);
      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [addedItemEvent, item.id]);

  useEffect(() => {
    if (!addedItemEvent || addedItemEvent.flashId !== item.id) {
      return;
    }

    // Scroll to this item when it was just added (uncompressed cart)
    wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // A new token (re)mounts the FlashOverlay below, replaying the animation
    // from scratch every time, regardless of the previous token/value
    setFlashToken(addedItemEvent.token);
  }, [addedItemEvent, item.id]);

  const [{ isDragging }, drag] = useDrag({
    item: { type: DragTypes.ITEM, selectedItems },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disallowDrag,
  });

  const [_, drop] = useDrop({
    accept: [DragTypes.TABLE, DragTypes.CHEQUE],
    canDrop: (item: any) => {
      return item.id;
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (disallowDrag) {
      setIsDragging(false);
      return;
    }
    setIsDragging(isDragging);
  }, [isDragging, disallowDrag]);

  const itemCount = 'count' in item ? item.count : item.fraction;

  return (
    <ItemWrapper
      onClick={onClick}
      selected={selected}
      preview={preview}
      isParkedOrder={window.location.hash.includes('parked-orders')}
      ref={(node) => {
        wrapperRef.current = node;
        if (selected) {
          drag(drop(node));
        }
      }}>
      {flashToken !== null && (
        <FlashOverlay key={flashToken} onAnimationEnd={() => setFlashToken(null)} />
      )}
      <ItemInnerWrapper>
        <Qty pulsing={pulsing}>{Math.round(itemCount * 100) / 100}</Qty>

        <Box>
          {item.title}

          <Box marginLeft="xs">
            {item.message && (
              <Paragraph level={5} color="var(--colorUISand)" bottom={4}>
                {item.message}
              </Paragraph>
            )}

            {/* We only show variations that cost money in TSR - it's irrelevant
                here if someone wants their steak medium rare once it's been sent
                to the kitchen. Compare against the bong view in QSR, not the
                table items, if this ever needs revisiting. */}
            {item.richVariations
              ?.filter((rv) => rv.price > 0)
              .map((variation) => (
                <Paragraph key={variation.id} level={5} color="var(--colorWhite)" bottom={4}>
                  + {variation.name}
                </Paragraph>
              ))}
          </Box>
        </Box>

        <PriceBox display="flex" alignItems="center">
          {item.courseId && <CourseTypeBox id={item.courseId} />}

          <PriceBox paddingLeft display="flex" flexDirection="column" alignItems="flex-end">
            {item.discount > 0 && !item.discountGroupId && (
              <DiscountName>
                {item.discountTitle ||
                  (item.discountId === BOOKINGS_DISCOUNT_ID
                    ? i18n('Discount.Booking')
                    : item.discountId === MEMBERSHIP_DISCOUNT_ID
                    ? i18n('Discount.Membership')
                    : item.discountId && offers.titleById[item.discountId]
                    ? offers.titleById[item.discountId]
                    : i18n('Discount.Manual'))}
              </DiscountName>
            )}

            <Box display="flex" gap="xs" alignItems="center">
              {item.discount > 0 && <InitialPrice>{currency.format(item.amount)}</InitialPrice>}
              {currency.format(item.amount - item.discount)}
            </Box>
          </PriceBox>
        </PriceBox>
      </ItemInnerWrapper>
    </ItemWrapper>
  );
};

export default ArticleItem;
